import React, { useContext } from 'react';
import { ThemeContext } from '../theme';


/**
 * Profile display picture with 3 variants.
 *
 * Variants:
 *   'adult' — circular photo, selectable ring on click
 *   'kid'   — circular photo + "Kid's Profile" badge overlapping the bottom edge
 *   'add'   — empty circle with profile icon, for adding a new profile
 */
export default function ProfileDP({ variant = 'adult', src, name, selected = false, onClick, scale = 1 }) {
  const theme = useContext(ThemeContext);
  const s = (n) => Math.round(n * scale);

  const avatarSize  = s(120);
  const badgePad    = `8px ${s(12)}px`;
  const badgeRadius = s(8);

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: s(16),
        width: avatarSize + s(40), // fixed width so circles are always equally spaced
        flexShrink: 0,
        cursor: 'pointer',
        userSelect: 'none',
        overflow: 'visible',
      }}
    >
      {/* Fixed-height avatar wrapper — same for all 3 variants so layout height is equal.
          position:relative + overflow:visible lets the badge peek below without
          adding to the layout height. */}
      <div style={{ position: 'relative', width: avatarSize, height: avatarSize, flexShrink: 0 }}>

        {/* Inner circle — separate div so overflow:hidden only clips the image,
            not the badge */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            overflow: 'hidden',
            backgroundColor: variant === 'add' ? '#e6e6e6' : 'transparent',
            outline: selected ? '2px solid #303F46' : '2px solid transparent',
            outlineOffset: '1px',
            transition: 'outline-color 0.15s',
          }}
        >
          {variant === 'add' ? (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width={Math.round(avatarSize * 0.30)} height={Math.round(avatarSize * 0.30)} viewBox="0 0 52 52" fill="none">
                <path d="M26 8v36M8 26h36" stroke={theme.text400} strokeWidth="4" strokeLinecap="round" />
              </svg>
            </div>
          ) : (
            <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
        </div>

        {/* Kid's Profile badge — absolutely positioned at the bottom edge of the
            fixed wrapper, centered. Half overlaps the circle, half hangs below.
            Because the wrapper has a fixed height, this does not affect layout. */}
        {(variant === 'kid' || variant === 'add') && (
          <div
            style={{
              position: 'absolute',
              bottom: -3,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#5d7b88',
              border: '1px solid #FFFFFF',
              borderRadius: badgeRadius,
              padding: badgePad,
              zIndex: 1,
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 73,
            }}
          >
            <span
              className="font-satoshi"
              style={{ fontSize: 9, lineHeight: 1, fontWeight: 500, color: '#FFFFFF', whiteSpace: 'nowrap' }}
            >
              {variant === 'kid' ? "Kid's Profile" : 'New'}
            </span>
          </div>
        )}
      </div>

      {/* Name label */}
      <span
        className="font-satoshi font-bold"
        style={{
          fontSize: s(18),
          lineHeight: `${s(24)}px`,
          color: theme.text100,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          ...(variant !== 'add' && { overflow: 'hidden', textOverflow: 'ellipsis' }),
        }}
      >
        {name}
      </span>
    </div>
  );
}
